import { AutoSpinMenu } from './AutoSpinMenu'
import { playSfx } from '../audio/sfx'
import { useGameStore } from '../store/gameStore'

interface Props {
  onSpin: () => void
}

function formatCoins(value: number): string {
  return value.toLocaleString('en-US')
}

export function ControlBar({ onSpin }: Props): React.JSX.Element {
  const phase = useGameStore((s) => s.phase)
  const game = useGameStore((s) => s.game)
  const balanceCoins = useGameStore((s) => s.balanceCoins)
  const betCoins = useGameStore((s) => s.betCoins)
  const allowedBets = useGameStore((s) => s.allowedBets)
  const setBet = useGameStore((s) => s.setBet)
  const mode = useGameStore((s) => s.mode)
  const setMode = useGameStore((s) => s.setMode)
  const chargeFor = useGameStore((s) => s.chargeFor)
  const displayWin = useGameStore((s) => s.displayWin)
  const gambleBusy = useGameStore((s) => s.gambleBusy)
  const autoSpinsLeft = useGameStore((s) => s.autoSpinsLeft)
  const stopAutoSpins = useGameStore((s) => s.stopAutoSpins)
  const turbo = useGameStore((s) => s.turbo)
  const toggleTurbo = useGameStore((s) => s.toggleTurbo)

  // A gamble step in flight locks the bar too: the round is not settled yet.
  const busy = phase !== 'idle' || gambleBusy
  const charge = chargeFor()
  const canAfford = balanceCoins >= charge

  const hasAnte = !!game?.modes.includes('ante')
  const hasBuy = !!game?.modes.includes('buy')
  const anteOn = mode === 'ante'
  const buyOn = mode === 'buy'
  const buyCost = chargeFor('buy')

  return (
    <div className="controls">
      <div className="meter meter--balance">
        <span className="meter__label">Balance</span>
        <span className="meter__value">{formatCoins(balanceCoins)}</span>
      </div>

      <div className="meter meter--bet">
        <span className="meter__label">Bet</span>
        <select
          className="bet-select"
          value={betCoins}
          disabled={busy}
          onChange={(e) => setBet(Number(e.target.value))}
        >
          {allowedBets.map((bet) => (
            <option key={bet} value={bet}>
              {formatCoins(bet)}
            </option>
          ))}
        </select>
      </div>

      {hasAnte && (
        <button
          className={`toggle ${anteOn ? 'toggle--on' : ''}`}
          disabled={busy || buyOn}
          onClick={() => {
            playSfx('button-click', 0.5)
            setMode(anteOn ? 'base' : 'ante')
          }}
          title="Ante Bet: bet ×1.25, scatters land more often"
        >
          <span className="toggle__label">Ante</span>
          <span className="toggle__sub">
            ×{game?.anteBet?.costMultiplier ?? 1.25} · {formatCoins(chargeFor('ante'))}
          </span>
        </button>
      )}

      {hasBuy && (
        <button
          className={`toggle toggle--buy ${buyOn ? 'toggle--on' : ''}`}
          disabled={busy || balanceCoins < buyCost}
          onClick={() => {
            playSfx('button-click', 0.5)
            setMode(buyOn ? 'base' : 'buy')
          }}
          title="Buy Free Spins"
        >
          <span className="toggle__label">Buy FS</span>
          <span className="toggle__sub">{formatCoins(buyCost)}</span>
        </button>
      )}

      <div className="meter meter--win">
        <span className="meter__label">Win</span>
        <span className={`meter__value ${displayWin > 0 ? 'meter__value--win' : ''}`}>
          {formatCoins(displayWin)}
        </span>
      </div>

      {/* Same footprint as Auto next to it: these two are settings, not the
          action — neither should compete with the spin button. */}
      <div className="meter meter--narrow">
        <span className="meter__label">Turbo</span>
        <button
          className={`mini-toggle ${turbo ? 'mini-toggle--on' : ''}`}
          onClick={() => {
            playSfx('button-click', 0.5)
            toggleTurbo()
          }}
          title="Turbo: fast spins and a short win reveal. Big Win still plays."
          type="button"
          aria-pressed={turbo}
        >
          {turbo ? 'ON' : 'OFF'}
        </button>
      </div>

      <div className="meter meter--narrow">
        <span className="meter__label">Auto</span>
        <AutoSpinMenu disabled={busy || !canAfford} />
      </div>

      {autoSpinsLeft > 0 ? (
        <button
          className="spin-button spin-button--stop"
          onClick={stopAutoSpins}
          title="Stop autospins"
        >
          STOP · {autoSpinsLeft}
        </button>
      ) : (
        <button
          className={`spin-button ${buyOn ? 'spin-button--buy' : ''} ${
            phase === 'spinning' ? 'spin-button--busy' : ''
          }`}
          onClick={onSpin}
          disabled={busy || !canAfford}
          title={!canAfford ? 'Insufficient funds' : buyOn ? `Buy Free Spins for ${formatCoins(charge)}` : 'Spin'}
        >
          {phase === 'spinning' ? (
            // Three static dots read as a truncated word. A running pulse is
            // the only thing on the bar that says "the server has not answered
            // yet", so it has to actually move.
            <span className="spin-button__dots" role="status" aria-label="Spinning">
              <i />
              <i />
              <i />
            </span>
          ) : buyOn ? (
            `BUY ${formatCoins(charge)}`
          ) : anteOn ? (
            `SPIN ${formatCoins(charge)}`
          ) : (
            'SPIN'
          )}
        </button>
      )}
    </div>
  )
}
